import { useState, useCallback } from 'react';
import { TopHeader } from '../../components/navigation/TopHeader';
import { BrowseFilters } from '../../components/browse/BrowseFilters';
import { ContentGrid } from '../../components/browse/ContentGrid';
import type { ContentItem, FilterCategory, SelectedFilter } from '../../types';

// Example filter categories - customize these for your app
const EXAMPLE_FILTER_CATEGORIES: FilterCategory[] = [
  {
    id: 'category',
    label: 'Category',
    options: [
      { id: 'featured', label: 'Featured' },
      { id: 'popular', label: 'Popular' },
      { id: 'new', label: 'New' },
      { id: 'trending', label: 'Trending' },
    ],
  },
  {
    id: 'type',
    label: 'Type',
    options: [
      { id: 'article', label: 'Article' },
      { id: 'video', label: 'Video' },
      { id: 'tutorial', label: 'Tutorial' },
      { id: 'resource', label: 'Resource' },
    ],
  },
];

// Example demo data - replace with API call in real app
const DEMO_ITEMS: ContentItem[] = [
  {
    id: 1,
    title: 'Getting Started Guide',
    description: 'Learn the basics of our platform and get up to speed quickly.',
    image_url: null,
    tags: [
      { id: 1, name: 'Featured', type: 'category' },
      { id: 2, name: 'Tutorial', type: 'type' },
    ],
    meta: { readTime: '5 min' },
    created_at: new Date().toISOString(),
  },
  {
    id: 2,
    title: 'Advanced Configuration',
    description: 'Deep dive into advanced settings and customization options.',
    image_url: null,
    tags: [
      { id: 3, name: 'Popular', type: 'category' },
      { id: 4, name: 'Article', type: 'type' },
    ],
    meta: { readTime: '12 min' },
    created_at: new Date().toISOString(),
  },
  {
    id: 3,
    title: 'Best Practices',
    description: 'Tips and tricks from power users to maximize your productivity.',
    image_url: null,
    tags: [
      { id: 5, name: 'Trending', type: 'category' },
      { id: 6, name: 'Resource', type: 'type' },
    ],
    meta: { readTime: '8 min' },
    created_at: new Date().toISOString(),
  },
  {
    id: 4,
    title: 'Video Walkthrough',
    description: 'A comprehensive video guide covering all major features.',
    image_url: null,
    tags: [
      { id: 7, name: 'New', type: 'category' },
      { id: 8, name: 'Video', type: 'type' },
    ],
    meta: { duration: '15 min' },
    created_at: new Date().toISOString(),
  },
  {
    id: 5,
    title: 'Integration Guide',
    description: 'Connect with your favorite tools and services seamlessly.',
    image_url: null,
    tags: [
      { id: 9, name: 'Featured', type: 'category' },
      { id: 10, name: 'Tutorial', type: 'type' },
    ],
    meta: { readTime: '10 min' },
    created_at: new Date().toISOString(),
  },
  {
    id: 6,
    title: 'API Documentation',
    description: 'Complete reference for developers building on our platform.',
    image_url: null,
    tags: [
      { id: 11, name: 'Popular', type: 'category' },
      { id: 12, name: 'Resource', type: 'type' },
    ],
    meta: { readTime: '20 min' },
    created_at: new Date().toISOString(),
  },
];

export function BrowsePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>([]);

  // In a real app, you would fetch items from an API
  const items = DEMO_ITEMS;
  const isLoading = false;

  const handleFilterToggle = useCallback(
    (categoryId: string, optionId: string, optionLabel: string, categoryLabel: string) => {
      setSelectedFilters((prev) => {
        const exists = prev.some((f) => f.categoryId === categoryId && f.optionId === optionId);
        if (exists) {
          return prev.filter((f) => !(f.categoryId === categoryId && f.optionId === optionId));
        }
        return [...prev, { categoryId, optionId, optionLabel, categoryLabel }];
      });
    },
    []
  );

  const handleFilterRemove = useCallback((categoryId: string, optionId: string) => {
    setSelectedFilters((prev) =>
      prev.filter((f) => !(f.categoryId === categoryId && f.optionId === optionId))
    );
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedFilters([]);
  }, []);

  const handleItemTap = useCallback((item: ContentItem) => {
    // Handle item tap - open detail modal, navigate to detail page, etc.
    console.log('Tapped item:', item);
    alert(`Tapped: ${item.title}`);
  }, []);

  const handleItemAction = useCallback((item: ContentItem) => {
    // Handle quick action - save, like, bookmark, etc.
    console.log('Action on item:', item);
    alert(`Action on: ${item.title}`);
  }, []);

  // Convert selected filters to simple string array for ContentGrid
  const activeFilterStrings = selectedFilters.map((f) => f.optionId);

  return (
    <div className="min-h-screen flex flex-col">
      <TopHeader
        title="Browse"
        subtitle={`${items.length} items available`}
      />

      {/* Search and filters */}
      <div className="px-4 pt-3 pb-2 border-b border-border bg-background-card">
        <BrowseFilters
          categories={EXAMPLE_FILTER_CATEGORIES}
          selectedFilters={selectedFilters}
          onFilterToggle={handleFilterToggle}
          onFilterRemove={handleFilterRemove}
          onClearAll={handleClearFilters}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search items..."
        />
      </div>

      {/* Content grid */}
      <div className="flex-1 px-4 sm:px-8 py-8 overflow-y-auto">
        <ContentGrid
          items={items}
          searchQuery={searchQuery}
          activeFilters={activeFilterStrings}
          isLoading={isLoading}
          onItemTap={handleItemTap}
          onItemAction={handleItemAction}
          emptyTitle="No items found"
          emptyDescription="Try adjusting your search or filters"
        />
      </div>
    </div>
  );
}
